import { useState } from "react";
import {
  useForm,
  FormProvider,
  Controller,
  type Resolver,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";
import { X, FileText, LayoutGrid } from "lucide-react";
import { TailwindModal } from "../../../components/TailwindModal";
import { FTPInput } from "../../../components-new";
import { addCanvasSchema, type AddCanvasFormData } from "./add-canvas-schema";
import { useCreateCanvas } from "../tars-hooks";
import { CANVAS_CREATE_DEFAULT_VERSION } from "../tars-constants";
import { cancelButtonSx, primaryButtonSx } from "./sidebar/add-source/addSourceStyles";

const MAX_TAGS = 10;
const TAG_PLACEHOLDER = "Type and press Enter";

interface AddCanvasModalProps {
  open: boolean;
  onClose: () => void;
  notebookId: number | null;
}

const CANVAS_TYPES: { id: AddCanvasFormData["type"]; label: string; Icon: typeof FileText }[] = [
  { id: "thinkSpace", label: "ThinkSpace", Icon: FileText },
  { id: "analytics", label: "Analytics", Icon: LayoutGrid },
];

function TagsInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  const onAdd = () => {
    const t = inputValue.trim();
    if (t && value.length < MAX_TAGS && !value.includes(t)) {
      onChange([...value, t]);
      setInputValue("");
    }
  };
  return (
    <div>
      <label className="mb-2 block text-[14px] font-medium text-[#1b1b1d]">Tags</label>
      <div className="flex min-h-[56px] flex-wrap items-center gap-2 rounded-xl border border-gray-200 px-3 py-2">
        {value.map((tag, i) => (
          <span
            key={i}
            className="flex items-center gap-1.5 rounded-full bg-gray-200 px-3 py-1.5 text-sm"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="rounded-full bg-black/20 px-1 text-xs font-bold hover:bg-black/40"
            >
              ×
            </button>
          </span>
        ))}
        {value.length < MAX_TAGS && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === ",") && (e.preventDefault(), onAdd())}
            className="min-w-[100px] flex-1 text-sm outline-none"
            placeholder={TAG_PLACEHOLDER}
          />
        )}
      </div>
    </div>
  );
}

export default function AddCanvasModal({ open, onClose, notebookId }: AddCanvasModalProps) {
  const { mutate: createCanvas, isLoading: isCreating } = useCreateCanvas();
  const methods = useForm<AddCanvasFormData>({
    resolver: yupResolver(addCanvasSchema) as Resolver<AddCanvasFormData>,
    defaultValues: { type: "analytics", name: "", tags: [] },
    mode: "onBlur",
  });
  const { handleSubmit, control, reset } = methods;

  const onSubmit = (data: AddCanvasFormData) => {
    if (!notebookId) return;
    createCanvas(
      {
        notebook_id: notebookId,
        name: data.name,
        type: data.type,
        version: CANVAS_CREATE_DEFAULT_VERSION,
        tags: data.tags ?? [],
        more: {},
      },
      { onSuccess: () => { reset(); onClose(); } }
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <TailwindModal isOpen={open} onClose={handleClose}>
      <FormProvider {...methods}>
        <div className="relative mx-auto w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-6 top-6 rounded-md p-1 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={20} className="text-gray-400" />
          </button>
          <h2 className="mb-6 text-[20px] font-bold text-[#1b1b1d]">Add new canvas</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="mb-2 block text-[14px] font-medium text-[#1b1b1d]">
                Choose canvas type
              </label>
              <div className="flex gap-3">
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <>
                      {CANVAS_TYPES.map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => field.onChange(id)}
                          className={`flex flex-1 flex-col items-center justify-center rounded-xl border py-4 px-6 transition ${
                            field.value === id
                              ? "border-[#6AF931] bg-[#1A1A1A] text-white"
                              : "border-gray-200 bg-white text-black hover:border-gray-300"
                          }`}
                        >
                          <Icon size={28} strokeWidth={1.5} />
                          <span className="mt-2 text-[14px] font-medium">{label}</span>
                        </button>
                      ))}
                    </>
                  )}
                />
              </div>
            </div>
            <FTPInput name="name" label="Name" placeholder="Name of the canvas" />
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <TagsInput value={field.value} onChange={field.onChange} />
              )}
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outlined" onClick={handleClose} sx={cancelButtonSx}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isCreating} sx={primaryButtonSx}>
                {isCreating ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </div>
      </FormProvider>
    </TailwindModal>
  );
}
